import React, { useEffect, useState } from "react";
import "../assets/styles/page.css";
import "../assets/styles/notificationSidebar.css";
import api from "../api/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const PAGE_NAME = "Notifications";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The global Axios interceptor handles 403 responses.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const getIcon = (type) => {
  switch (String(type || "").toLowerCase()) {
    case "order":
      return "pi pi-shopping-bag";

    case "payment":
      return "pi pi-credit-card";

    case "refund":
      return "pi pi-refresh";

    case "invoice":
      return "pi pi-file";

    case "cart":
      return "pi pi-shopping-cart";

    case "customer":
      return "pi pi-user";

    case "system":
      return "pi pi-cog";

    case "dispute":
      return "pi pi-comments";

    default:
      return "pi pi-bell";
  }
};

const formatTime = (date) => {
  if (!date) return "";

  const timestamp = new Date(date).getTime();

  if (Number.isNaN(timestamp)) return "";

  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return "Just now";

  if (minutes < 60) {
    return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  }

  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  if (days === 1) return "Yesterday";

  if (days < 7) {
    return `${days} days ago`;
  }

  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normaliseMetadata = (metadata) => {
  if (!metadata) return {};

  if (typeof metadata === "object") {
    return metadata;
  }

  if (typeof metadata === "string") {
    try {
      return JSON.parse(metadata);
    } catch {
      return {};
    }
  }

  return {};
};

const Notifications = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);

      const params = {};

      if (filter === "read") {
        params.is_read = true;
      }

      if (filter === "unread") {
        params.is_read = false;
      }

      const response = await api.get(
        "/admin/notifications",
        {
          headers: getPermissionHeaders("view"),
          params,
        }
      );

      setNotifications(
        Array.isArray(response.data?.data)
          ? response.data.data
          : []
      );

      setUnreadCount(
        Number(response.data?.unread_count || 0)
      );
    } catch (error) {
      setNotifications([]);

      showRequestError(
        error,
        "Failed to fetch notifications."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const markAsRead = async (notificationId) => {
    if (!notificationId || markingId) return;

    try {
      setMarkingId(notificationId);

      await api.put(
        `/admin/notifications/${notificationId}/read`,
        {},
        {
          // Marking a notification as read changes data,
          // therefore it uses the edit permission.
          headers: getPermissionHeaders("edit"),
        }
      );

      setNotifications((previous) =>
        previous
          .map((notification) =>
            Number(notification.notification_id) ===
            Number(notificationId)
              ? {
                  ...notification,
                  is_read: true,
                  read_at:
                    notification.read_at ||
                    new Date().toISOString(),
                }
              : notification
          )
          .filter((notification) => {
            if (filter !== "unread") return true;

            return !notification.is_read;
          })
      );

      setUnreadCount((previous) =>
        Math.max(0, previous - 1)
      );
    } catch (error) {
      showRequestError(
        error,
        "Failed to mark notification as read."
      );
    } finally {
      setMarkingId(null);
    }
  };

  const markAllAsRead = async () => {
    if (
      markingAll ||
      loading ||
      unreadCount <= 0
    ) {
      return;
    }

    try {
      setMarkingAll(true);

      await api.put(
        "/admin/notifications/mark-all-read",
        {},
        {
          headers: getPermissionHeaders("edit"),
        }
      );

      toast.success(
        "All notifications marked as read."
      );

      setUnreadCount(0);

      if (filter === "unread") {
        setNotifications([]);
      } else {
        setNotifications((previous) =>
          previous.map((notification) => ({
            ...notification,
            is_read: true,
            read_at:
              notification.read_at ||
              new Date().toISOString(),
          }))
        );
      }
    } catch (error) {
      showRequestError(
        error,
        "Failed to mark all notifications as read."
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (markingId === notification.notification_id) return;

    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }

    if (notification.action_url) {
      navigate(String(notification.action_url).replace(/^\/admin/, ""));
    }
  };

  return (
    <section className="admin-page notifications-page">
      <div className="page-header">
        <div>
          <p>Updates</p>
          <h1>Notifications</h1>
        </div>

        <button
          type="button"
          className="primary-action"
          onClick={markAllAsRead}
          disabled={
            markingAll ||
            loading ||
            unreadCount <= 0
          }
        >
          {markingAll
            ? "Updating..."
            : "Mark All Read"}
        </button>
      </div>

      <div className="page-card notification-centre-card">
        <div className="notification-centre-header">
          <div>
            <h2>Notification Centre</h2>

            <p>
              {loading
                ? "Loading notifications..."
                : ``}
            </p>
          </div>

          <div className="notification-filter-tabs">
            <button
              type="button"
              className={
                filter === "all" ? "active" : ""
              }
              onClick={() => setFilter("all")}
              disabled={loading}
            >
              All
            </button>

            <button
              type="button"
              className={
                filter === "unread"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setFilter("unread")
              }
              disabled={loading}
            >
              Unread
              {unreadCount > 0 && (
                <span>{unreadCount}</span>
              )}
            </button>

            <button
              type="button"
              className={
                filter === "read" ? "active" : ""
              }
              onClick={() => setFilter("read")}
              disabled={loading}
            >
              Read
            </button>
          </div>
        </div>

        <div className="notification-page-list">
          {loading ? (
            <div className="empty-notification-state">
              <i className="pi pi-spin pi-spinner" />
              <h3>Loading notifications</h3>
              <p>
                Please wait while your updates are
                loaded.
              </p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="empty-notification-state">
              <i className="pi pi-bell" />
              <h3>No notifications found</h3>
              <p>You are all caught up.</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const metadata =
                normaliseMetadata(
                  notification.metadata
                );

              const isMarking =
                Number(markingId) ===
                Number(
                  notification.notification_id
                );

              return (
                <button
                  type="button"
                  key={
                    notification.notification_id
                  }
                  className={`notification-page-item ${
                    !notification.is_read
                      ? "unread"
                      : ""
                  } ${
                    isMarking
                      ? "notification-updating"
                      : ""
                  }`}
                  onClick={() =>
                    handleNotificationClick(
                      notification
                    )
                  }
                  disabled={isMarking}
                >
                  <div className="notification-icon">
                    <i
                      className={getIcon(
                        notification.type
                      )}
                    />
                  </div>

                  <div className="notification-content">
                    <div>
                      <h3>
                        {notification.title ||
                          "Notification"}
                           <small style={{ fontSize: "12px", marginLeft: "10px" }}>
                            {formatTime(
                              notification.createdAt ||
                                notification.created_at
                            )}
                          </small>
                      </h3>

                      {!notification.is_read && (
                        <span>
                          {isMarking
                            ? "Updating"
                            : "New"}
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <p style={{ marginBottom: "0px" }}>
                        {notification.message ||
                          "No notification message available."}

                          {metadata.order_number && (
                            <small style={{ display: "block", textAlign: "left" }}>
                              Order:{" "}
                              {metadata.order_number}
                            </small>
                          )}

                          {metadata.invoice_number && (
                            <small style={{ display: "block", textAlign: "left" }}>
                              Invoice:{" "}
                              {metadata.invoice_number}
                            </small>
                          )}

                          {metadata.dispute_id && (
                            <small style={{ display: "block", textAlign: "left" }}>
                              Dispute: #
                              {metadata.dispute_id}
                            </small>
                          )}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default Notifications;
